import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import axios from 'axios';

import {
  changePassword,
  getSettings,
  putSettingsOption,
} from '../../api/settings';

import { REQUEST } from '../../constants/request';
import { SETTINGS } from '../../components/Settings/constants/settings';
import {
  INITIAL_USER_SETTINGS,
  SETTINGS_ENDORSEMENT_OPTION,
  SETTINGS_FREE_OPTION,
  SETTINGS_INCOMING_REBUZZ_OPTIONS,
  SETTINGS_OUTGOING_REBUZZ_ENDORS_OPTION,
  SETTINGS_OUTGOING_REBUZZ_OPTION,
  SETTINGS_OUTGOING_REBUZZ_OPTIONS,
  SETTINGS_REBUZZ_OPTION,
  STORE_INITIAL_STATE,
} from '../../components/Settings/constants/initialStates';
import { INCOMING_REBUZZ_OPTIONS } from '../../components/Settings/constants/incomingRebuzzOptions';
import { OUTGOING_REBUZZ_OPTIONS } from '../../components/Settings/constants/outgoingRebuzzOptions';

export const fetchMeSettings = createAsyncThunk(
  'currentUser/fetchMeSettings',
  async (formData, { signal, rejectWithValue }) => {
    const source = axios.CancelToken.source();
    signal.addEventListener('abort', () => {
      source.cancel();
    });
    try {
      const response = await getSettings({
        cancelToken: source.token,
      });

      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.status);
    }
  },
  {
    condition: (param, { getState }) => {
      const currentLoading =
        getState().settings.currentLoadings?.fetchMeSettings || REQUEST.idle;

      return currentLoading !== REQUEST.pending;
    },
  },
);

export const changeUserPassword = createAsyncThunk(
  'comments/changePassword',
  async (urlParams, { signal, rejectWithValue }) => {
    const source = axios.CancelToken.source();
    signal.addEventListener('abort', () => {
      source.cancel();
    });
    try {
      const response = await changePassword(urlParams, {
        cancelToken: source.token,
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.status);
    }
  },
);

export const changeSettingsOption = createAsyncThunk(
  `users/changeSettingsOption`,
  async ({ data, additionalPath }, { signal, rejectWithValue }) => {
    const source = axios.CancelToken.source();
    signal.addEventListener('abort', () => {
      source.cancel();
    });
    try {
      const response = await putSettingsOption(
        { data, additionalPath },
        {
          cancelToken: source.token,
        },
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.status);
    }
  },
);

export const SettingsSlice = createSlice({
  name: 'settings',
  initialState: STORE_INITIAL_STATE,
  reducers: {
    changeBoostsPeriodicityRebuzzStatistics: (state, { payload }) => {
      const { option, value } = payload;
      state[SETTINGS.REBUZZ_STATISTICS].total[option].period = value;
    },
    patchIncomingRebuzz: (state, { payload }) => {
      state[SETTINGS.INCOMING_REBUZZ][payload.option] = {
        ...state[SETTINGS.INCOMING_REBUZZ][payload.option],
        [payload.field]: payload.value,
      };
    },
    switchIncomingRebuzzSettings: (state, { payload }) => {
      if (!payload) {
        state[SETTINGS.INCOMING_REBUZZ] = {
          ...state[SETTINGS.INCOMING_REBUZZ],
          ...SETTINGS_INCOMING_REBUZZ_OPTIONS,
        };
      }

      state[SETTINGS.INCOMING_REBUZZ].isEnabled = payload;
    },
    switchIncomingRebuzzSettingsOption: (state, { payload }) => {
      state[SETTINGS.INCOMING_REBUZZ][payload.option].isEnabled = payload.value;

      // reset all fields if switch is false
      if (payload.value) return;

      if (
        [
          INCOMING_REBUZZ_OPTIONS.PAID_CONTENT,
          INCOMING_REBUZZ_OPTIONS.PAID_CONTENT,
        ].includes(payload.option)
      ) {
        state[SETTINGS.INCOMING_REBUZZ][payload.option] = {
          ...SETTINGS_REBUZZ_OPTION,
        };
      }
      if (
        [
          INCOMING_REBUZZ_OPTIONS.FREE_CONTENT,
          INCOMING_REBUZZ_OPTIONS.FREE_PROMOTION,
        ].includes(payload.option)
      ) {
        state[SETTINGS.INCOMING_REBUZZ][payload.option] = {
          ...SETTINGS_FREE_OPTION,
        };
      }
      if (payload.option === INCOMING_REBUZZ_OPTIONS.ENDORSEMENT) {
        state[SETTINGS.INCOMING_REBUZZ][payload.option] = {
          ...SETTINGS_ENDORSEMENT_OPTION,
        };
      }
    },
    patchOutgoingRebuzz: (state, { payload }) => {
      if (payload.option) {
        state[SETTINGS.OUTGOING_REBUZZ][payload.option] = {
          ...state[SETTINGS.OUTGOING_REBUZZ][payload.option],
          [payload.field]: payload.value,
        };
      } else {
        state[SETTINGS.OUTGOING_REBUZZ] = {
          ...state[SETTINGS.OUTGOING_REBUZZ],
          [payload.field]: payload.value,
        };
      }
    },
    switchOutgoingRebuzzSettings: (state, { payload }) => {
      if (!payload) {
        state[SETTINGS.OUTGOING_REBUZZ] = {
          ...state[SETTINGS.OUTGOING_REBUZZ],
          ...SETTINGS_OUTGOING_REBUZZ_OPTIONS,
        };
      }

      state[SETTINGS.OUTGOING_REBUZZ].isEnabled = payload;
    },
    switchOutgoingRebuzzSettingsOption: (state, { payload }) => {
      state[SETTINGS.OUTGOING_REBUZZ][payload.option].isEnabled = payload.value;

      // reset all fields if switch is false
      if (payload.value) return;

      if (
        [
          OUTGOING_REBUZZ_OPTIONS.CONTENT,
          OUTGOING_REBUZZ_OPTIONS.PROMOTION,
        ].includes(payload.option)
      ) {
        state[SETTINGS.OUTGOING_REBUZZ][payload.option] = {
          ...SETTINGS_OUTGOING_REBUZZ_OPTION,
        };
      }
      if (payload.option === INCOMING_REBUZZ_OPTIONS.ENDORSEMENT) {
        state[SETTINGS.OUTGOING_REBUZZ][payload.option] = {
          ...SETTINGS_OUTGOING_REBUZZ_ENDORS_OPTION,
        };
      }
    },
    setCheckboxBoolean: (state, { payload }) => {
      const { stateKey, stateChildKey, property } = payload;

      if (stateChildKey) {
        state[stateKey][stateChildKey][property] =
          !state[stateKey][stateChildKey][property];
      } else {
        state[stateKey][property] = !state[stateKey][property];
      }
    },
    addPersonalDataTag: (state, { payload }) => {
      if (payload.variant === 'topic') {
        state.personalData.topicTags.push(payload.newTag);
      } else {
        state.personalData.locationTags.push(payload.newTag);
      }
    },
    removePersonalDataTag: (state, { payload }) => {
      if (payload.variant === 'topic') {
        state.personalData.topicTags = state.personalData.topicTags.filter(
          topicTag => topicTag !== payload.deleteTag,
        );
      } else {
        state.personalData.locationTags =
          state.personalData.locationTags.filter(
            locationTag => locationTag !== payload.deleteTag,
          );
      }
    },
    addPersonalDataSuggestedTag: (state, { payload }) => {
      if (payload.variant === 'topic') {
        state.personalData.suggestedTopicTags.push(payload.newTag);
      } else {
        state.personalData.suggestedLocationTags.push(payload.newTag);
      }
    },
    removePersonalDataSuggestedTag: (state, { payload }) => {
      if (payload.variant === 'topic') {
        state.personalData.suggestedTopicTags =
          state.personalData.suggestedTopicTags.filter(
            suggestedTopicTag => suggestedTopicTag !== payload.deleteTag,
          );
      } else {
        state.personalData.suggestedLocationTags =
          state.personalData.suggestedLocationTags.filter(
            suggestedLocationTag => suggestedLocationTag !== payload.deleteTag,
          );
      }
    },
    filterPersonalDataSuggestedTags: (state, { payload }) => {
      if (payload === 'topic') {
        state.personalData.suggestedTopicTags =
          state.personalData.suggestedTopicTags.filter(
            suggestedTopicTag =>
              !state.personalData.topicTags.includes(suggestedTopicTag),
          );
      } else {
        state.personalData.suggestedLocationTags =
          state.personalData.suggestedLocationTags.filter(
            suggestedLocationTag =>
              !state.personalData.locationTags.includes(suggestedLocationTag),
          );
      }
    },
    changePersonalDataPhoneNumber: (state, { payload }) => {
      state.personalData.phoneNumber = payload.phoneNumber;
    },
    changePersonalDataGender: (state, { payload }) => {
      state.personalData.gender = payload;
    },
    changePersonalDataDateOfBirth: (state, { payload }) => {
      state.personalData.dateOfBirth = payload;
    },
    setInputChannels: (state, action) => {
      state.personalData.inputChannels = action.payload;
    },
    setSelectedChannelTags: (state, action) => {
      state.personalData.selectedChannelTags = [...action.payload];
    },
    // TODO: remove when backend is ready
    setTwoFactorAuthentication: (state, { payload }) => {
      state.security.twoFactorAuth = payload;
    },
    // TODO: remove when backend is ready
    setPublicVisibility: (state, { payload }) => {
      state.buzz.publicVisibility = payload;
    },
    // TODO: remove when backend is ready
    setContentSortOrder: (state, { payload }) => {
      state.content.sortOrder = payload;
    },
    // TODO: remove when backend is ready
    setContentBuzzView: (state, { payload }) => {
      state.content.buzzView = payload;
    },
    // TODO: remove when backend is ready
    setMutedWordsList: (state, { payload }) => {
      state.filter.mutedWords = payload;
    },
    // TODO: remove when backend is ready
    setMessageOption: (state, { payload }) => {
      state.message.allowFrom = payload;
    },
    // TODO: remove when backend is ready
    setAllowedUsers: (state, { payload }) => {
      state.message.allowedUsers = payload;
    },
    // TODO: remove when backend is ready
    setTimelinePreference: (state, { payload }) => {
      state.publisher.canPublishToTimeline = payload;
    },
    saveCurrentUserSettings: (state, { payload }) => {
      state.rebuzzer.users.byId[payload.userId] = payload.settings;
    },
    resetSettingsPage: () => STORE_INITIAL_STATE,
  },
  extraReducers: builder => {
    builder.addCase(fetchMeSettings.pending, (state, { meta }) => {
      state.currentLoadings.fetchMeSettings = REQUEST.pending;
      state.currentRequestIds.fetchMeSettings = meta.requestId;
    });
    builder.addCase(fetchMeSettings.fulfilled, (state, { meta, payload }) => {
      if (
        state.currentLoadings.fetchMeSettings === REQUEST.pending &&
        state.currentRequestIds.fetchMeSettings === meta.requestId
      ) {
        state.buzz = { ...state.buzz, ...payload.buzz };
        state.personalData = { ...state.personalData, ...payload.content };
        state.emailNotification = {
          ...state.emailNotification,
          ...payload.emailNotification,
        };
        state.filter = {
          ...state.filter,
          ...payload.filter,
        };
        state.message = {
          ...state.message,
          ...payload.message,
          // TODO: remove when backend is ready
          allowedUsers: [
            ...state.message.allowedUsers,
            ...payload.message.allowedUsers,
          ],
        };
        state.security = {
          ...state.security,
          ...payload.security,
        };
        state.session = {
          ...state.session,
          ...payload.session,
        };

        state.currentLoadings.fetchMeSettings = REQUEST.idle;
        state.currentRequestIds.fetchMeSettings = undefined;
      }
    });
    builder.addCase(fetchMeSettings.rejected, (state, { error }) => {
      state.currentLoadings.fetchMeSettings = REQUEST.idle;
      state.currentRequestIds.fetchMeSettings = undefined;
      state.currentErrors.fetchMeSettings = error;
    });
    builder.addCase(changeUserPassword.pending, (state, action) => {
      state.currentLoadings.changeUserPassword = REQUEST.pending;
      state.currentRequestIds.changeUserPassword = action.meta.requestId;
    });
    builder.addCase(changeUserPassword.fulfilled, (state, action) => {
      const { meta } = action;
      const { requestId } = meta;
      if (
        state.currentLoadings.changeUserPassword === REQUEST.pending &&
        state.currentRequestIds.changeUserPassword === requestId
      ) {
        state.currentLoadings.changeUserPassword = REQUEST.idle;
        state.currentRequestIds.changeUserPassword = undefined;
      }
    });
    builder.addCase(changeUserPassword.rejected, (state, action) => {
      const { requestId } = action.meta;
      if (
        state.currentLoadings.changeUserPassword === REQUEST.pending &&
        state.currentRequestIds.changeUserPassword === requestId
      ) {
        state.currentLoadings.changeUserPassword = REQUEST.idle;
        state.errors.changeUserPassword = action.payload;
        state.currentRequestIds.changeUserPassword = undefined;
      }
    });
    builder.addCase(changeSettingsOption.pending, (state, { meta }) => {
      const { data, additionalPath } = meta.arg;
      const dataStr = Object.keys(data).join('/');
      const UID = `${additionalPath}.${dataStr}`;
      state.currentRequestIds[UID] = meta.requestId;
      state.currentLoadings[UID] = REQUEST.pending;
    });
    builder.addCase(
      changeSettingsOption.fulfilled,
      (state, { meta, payload }) => {
        const { data, additionalPath } = meta.arg;
        const dataStr = Object.keys(data).join('/');
        const UID = `${additionalPath}.${dataStr}`;
        if (
          state.currentLoadings[UID] === REQUEST.pending &&
          state.currentRequestIds[UID] === meta.requestId
        ) {
          state.buzz = payload.buzz;
          state.personalData = { ...state.personalData, ...payload.content };
          state.emailNotification = {
            ...state.emailNotification,
            ...payload.emailNotification,
          };
          state.filter = {
            ...state.filter,
            ...payload.filter,
          };
          state.message = {
            ...state.message,
            ...payload.message,
          };
          state.security = {
            ...state.security,
            ...payload.security,
          };
          state.session = {
            ...state.session,
            ...payload.session,
          };
          state.currentLoadings[UID] = REQUEST.idle;
          delete state.currentRequestIds[UID];
        }
      },
    );
    builder.addCase(
      changeSettingsOption.rejected,
      (state, { meta, payload }) => {
        const { data, additionalPath } = meta.arg;
        const dataStr = Object.keys(data).join('/');
        const UID = `${additionalPath}.${dataStr}`;
        if (
          state.currentLoadings[UID] === REQUEST.pending &&
          state.currentRequestIds[UID] === meta.requestId
        ) {
          state.currentErrors[UID] = payload;
          delete state.currentLoadings[UID];
          delete state.currentRequestIds[UID];
        }
      },
    );
  },
});

export const selectorPersonalDataSettings = state =>
  state.settings[SETTINGS.PERSONAL_DATA];
export const selectorActivityFeedSettings = state =>
  state.settings[SETTINGS.ACTIVITY_FEED];
export const selectorBoostSettings = state => state.settings[SETTINGS.BOOST];
export const selectorBuzzSettings = state => state.settings[SETTINGS.BUZZ];
export const selectorContentSettings = state =>
  state.settings[SETTINGS.CONTENT];
export const selectorEmailNotificationSettings = state =>
  state.settings[SETTINGS.EMAIL_NOTIFICATION];
export const selectorFilterSettings = state => state.settings[SETTINGS.FILTER];
export const selectorMessageSettings = state =>
  state.settings[SETTINGS.MESSAGE];
export const selectorPasswordSettings = state =>
  state.settings[SETTINGS.PASSWORD];
export const selectorSecuritySettings = state =>
  state.settings[SETTINGS.SECURITY];
export const selectorSessionSettings = state =>
  state.settings[SETTINGS.SESSION];
export const selectorLearnMoreAboutPrivacyOnGmantraSettings = state =>
  state.settings[SETTINGS.LEARN_MORE_ABOUT_PRIVACY_ON_GMANTRA];
export const selectorConnectedAccountsSettings = state =>
  state.settings[SETTINGS.CONNECTED_ACCOUNTS];
export const selectorMessagesSettings = state =>
  state.settings[SETTINGS.MESSAGES];
export const selectorInvitesSettings = state =>
  state.settings[SETTINGS.INVITES];
export const selectorPublisherSettings = state =>
  state.settings[SETTINGS.PUBLISHER];
export const selectorRebuzzerSettings = (state, userId) =>
  state.settings[SETTINGS.REBUZZER].users.byId[userId] || INITIAL_USER_SETTINGS;

export const {
  changeBoostsPeriodicityRebuzzStatistics,
  patchIncomingRebuzz,
  switchIncomingRebuzzSettings,
  switchIncomingRebuzzSettingsOption,
  patchOutgoingRebuzz,
  switchOutgoingRebuzzSettings,
  switchOutgoingRebuzzSettingsOption,
  addPersonalDataTag,
  removePersonalDataTag,
  addPersonalDataSuggestedTag,
  removePersonalDataSuggestedTag,
  filterPersonalDataSuggestedTags,
  changePersonalDataPhoneNumber,
  changePersonalDataGender,
  changePersonalDataDateOfBirth,
  setCheckboxBoolean,
  setInputChannels,
  setSelectedChannelTags,
  setTwoFactorAuthentication,
  setMessageOption,
  setPublicVisibility,
  setContentSortOrder,
  setContentBuzzView,
  setMutedWordsList,
  setAllowedUsers,
  saveCurrentUserSettings,
  setTimelinePreference,
  resetSettingsPage,
} = SettingsSlice.actions;

export default SettingsSlice.reducer;
